import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { LoanStatusType, LoanType } from '../common/LoanConstants';
import { BfiDataGrid, Column, MasterDetail, LoanCommitOrApprovalColumn } from '../../common/SharedComponents';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import {
    getLoanTypeShortName,
    getStatusTypeShortName,
    onBorrowerCellRender,
    onActionCellRender,
    onRenderPortfolioGrid,
    onRowPrepared,
    OnOpenLoanDetail,
    OnOpenLoanSummary,
} from '../common/LoanFunctions';
import { addressTooltipText } from '../../common/SharedFunctions';
import { Tooltip } from 'devextreme-react/tooltip';
import { LoanRepaidIndicatorColumn } from '../common/components/LoanRepaidIndicatorColumn';
import { LoanApprovedOrNoteDateColumn } from '../common/components/LoanApprovedOrNoteDateColumn';
import { onStructureTypeCellRender } from '../common/functions/onStructureTypeCellRender';
import { onLoanTermCellRender } from '../common/functions/onLoanTermCellRender';
import { onExtensionCellRender } from '../common/functions/onExtensionCellRender';
import { onRepaidDateCellRender } from '../common/functions/onRepaidDateCellRender';

interface LoanSearchTableParameters {
    statusId: LoanStatusType;
    loanTypeId: LoanType;
    gridStore: any;
}

export const LoanSearchTable: React.FC<LoanSearchTableParameters> = ({
    statusId,
    loanTypeId = LoanType.Construction,
    gridStore,
}) => {
    const searchState = useSelector((state) => state.searchState);
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const location = useLocation();

    const [dataStore, set_dataStore] = useState(gridStore?.store);

    const dataFunction = (criteria) => {
        criteria.loanTypeId = loanTypeId;
        criteria.statusId = statusId;
        return loanApi.getGrid_Search(criteria);
    };

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.loan);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    const onCityCellRender = (e) => {
        const targetId = `city_${e.data.id}`;
        if (e.data.loanTypeId === LoanType.MasterNote) {
            const text = addressTooltipText(e.data.borrower.address, 'Borrower Address');
            return (
                <>
                    <div
                        data-tip
                        data-for={targetId}
                    >
                        {e.data.borrowerCity}
                    </div>
                    {!e.data.isPortfolio && (
                        <Tooltip
                            target={`[data-for="${targetId}"]`}
                            showEvent='mouseenter'
                            hideEvent='mouseleave'
                            hideOnOutsideClick={false}
                        >
                            {text}
                        </Tooltip>
                    )}
                </>
            );
        } else {
            const text = addressTooltipText(e.data.address, 'Property Address');
            return (
                <>
                    <div
                        data-tip
                        data-for={targetId}
                    >
                        {e.data.propertyCity}
                    </div>
                    {!e.data.isPortfolio && (
                        <Tooltip
                            target={`[data-for="${targetId}"]`}
                            showEvent='mouseenter'
                            hideEvent='mouseleave'
                            hideOnOutsideClick={false}
                        >
                            {text}
                        </Tooltip>
                    )}
                </>
            );
        }
    };

    const onStateCellRender = (e) => {
        const targetId = `state_${e.data.id}`;
        if (e.data.loanTypeId === LoanType.MasterNote) {
            const text = addressTooltipText(e.data.borrower.address, 'Borrower Address');
            return (
                <>
                    <div
                        data-tip
                        data-for={targetId}
                    >
                        {e.data.borrowerState}
                    </div>
                    {!e.data.isPortfolio && (
                        <Tooltip
                            target={`[data-for="${targetId}"]`}
                            showEvent='mouseenter'
                            hideEvent='mouseleave'
                            hideOnOutsideClick={false}
                        >
                            {text}
                        </Tooltip>
                    )}
                </>
            );
        } else {
            const text = addressTooltipText(e.data.address, 'Property Address');
            return (
                <>
                    <div
                        data-tip
                        data-for={targetId}
                    >
                        {e.data.propertyState}
                    </div>
                    {!e.data.isPortfolio && (
                        <Tooltip
                            target={`[data-for="${targetId}"]`}
                            showEvent='mouseenter'
                            hideEvent='mouseleave'
                            hideOnOutsideClick={false}
                        >
                            {text}
                        </Tooltip>
                    )}
                </>
            );
        }
    };

    const onLoanTypeCellRender = (e) => {
        if (e.data.isRental) {
            return getLoanTypeShortName(LoanType.Rental);
        }
        return getLoanTypeShortName(e.data.loanTypeId);
    };

    const onStatusTypeCellRender = (e) => {
        return getStatusTypeShortName(e.data.statusId);
    };

    const shouldShowStructureType = (e) => {
        return e.data.loanTypeId != LoanType.Land;
    };

    return (
        <BfiDataGrid
            dataStore={dataStore}
            applicationInsights={applicationInsights}
            onRowPrepared={onRowPrepared}
            onCellDblClick={(e) =>
                statusId === LoanStatusType.Approved ||
                statusId === LoanStatusType.Outstanding ||
                statusId === LoanStatusType.Repaid
                    ? OnOpenLoanDetail(e, location)
                    : OnOpenLoanSummary(e, location)
            }
        >
            <Column
                dataField='loanTypeId'
                caption='Loan Type'
                cellRender={onLoanTypeCellRender}
                alignment='left'
                width={80}
            />
            <Column
                dataField='statusId'
                caption='Status'
                cellRender={onStatusTypeCellRender}
                alignment='left'
                width={80}
            />
            <Column
                dataField='borrowerEntity'
                caption='Entity'
                cellRender={onBorrowerCellRender}
                name='borrowerEntity'
                minWidth={120}
            />
            <Column
                dataField='propertyCity'
                caption='City'
                cellRender={onCityCellRender}
                width={90}
            />
            <Column
                dataField='propertyState'
                caption='State'
                cellRender={onStateCellRender}
                width={70}
            />
            <Column
                dataField='bfiNumber'
                caption='BFI #'
                dataType='string'
                width={120}
            />
            <Column
                dataField='loanNumber'
                caption='Loan #'
                dataType='string'
                width={80}
            />
            <Column
                dataField='structureTypeId'
                caption='Structure Type'
                dataType='string'
                cellRender={onStructureTypeCellRender}
                width={120}
            />
            {LoanCommitOrApprovalColumn({ statusId })}
            {LoanApprovedOrNoteDateColumn({ statusId })}
            <Column
                dataField='initialTermMonths'
                caption='Term'
                dataType='string'
                width={60}
                alignment={'left'}
                cellRender={onLoanTermCellRender}
            />
            {LoanRepaidIndicatorColumn({ statusId })}
            {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='repaidDate'
                    caption='Repaid Date'
                    dataType='string'
                    width={110}
                    alignment={'left'}
                    cellRender={onRepaidDateCellRender}
                />
            )}
            {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='totalRepaidAmount'
                    caption='Repaid Amt'
                    dataType='number'
                    format='currency'
                    width={110}
                    alignment={'left'}
                />
            )}
            {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='extensionNumber'
                    caption='Ext.'
                    dataType='string'
                    width={40}
                    alignment='left'
                    cellRender={onExtensionCellRender}
                />
            )}
            {/* TODO: Future work related to Defaults */}
            {/* {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='defaults'
                    caption='Defaults'
                    dataType='string'
                    width={110}
                />
            )} */}
            <Column
                dataField='pointOfContact'
                caption='POC'
                dataType='string'
                width={120}
            />
            <Column
                dataField='ownerName'
                caption='User'
                dataType='string'
                width={120}
            />
            <Column
                dataField='broker'
                dataType='string'
                width={120}
            />
            <Column
                alignment='center'
                cellRender={onActionCellRender}
                width={50}
                allowReordering={false}
                allowSorting={false}
                allowResizing={false}
                type='actions'
                fixed={true}
                fixedPosition={'right'}
            />
            <MasterDetail
                enabled={true}
                render={(e) => onRenderPortfolioGrid(e, statusId, shouldShowStructureType(e))}
            />
        </BfiDataGrid>
    );
};
